import { db, auth } from "@/firebase";
import {
  doc,
  query,
  collection,
  where,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  orderBy,
} from "firebase/firestore";
import { contentConverter, templateConverter } from "./firestore_converters";
import { Template } from "@/types/Message";
import type { FeatureFlagKey } from "@/featureFlags";

function assertRegisteredUser(): void {
  if (!auth.currentUser || auth.currentUser.isAnonymous) {
    throw new Error(
      "Operation not allowed: user is not signed in with a registered account.",
    );
  }
}

export async function updateUserFeatureFlag(
  userID: string,
  flag: FeatureFlagKey,
  enabled: boolean,
) {
  assertRegisteredUser();
  const userReference = doc(db, "users", userID);
  await updateDoc(userReference, {
    [`featureFlags.${flag}`]: enabled,
  });
}

export interface UserAPIKey {
  id?: string;
  name?: string;
  key: string;
}

export async function getUserAPIKeys(userID: string): Promise<UserAPIKey[]> {
  const apiKeysData = await getDocs(collection(db, "users", userID, "apiKeys"));
  const apiKeysFromDB = apiKeysData.docs.map((doc) => {
    const data = doc.data();
    return {
      id: doc.id,
      key: data.key as string,
      ...(data.name ? { name: data.name as string } : {}),
    };
  });
  return apiKeysFromDB;
}

export async function updateUserName(userID: string, username: string) {
  assertRegisteredUser();
  const trimmed = username.trim();
  if (!trimmed) {
    return false;
  }
  // Check if username in use by another user:
  const usersWithUsername = await getDocs(
    query(collection(db, "users"), where("username", "==", trimmed)),
  );
  const isTakenByOther = usersWithUsername.docs.some(
    (doc) => doc.id !== userID,
  );
  if (isTakenByOther) {
    return false;
  } else {
    const userReference = doc(db, "users", userID);
    await updateDoc(userReference, { username: trimmed });
    return true;
  }
}

export async function updateUserLanguage(userID: string, language: string) {
  assertRegisteredUser();
  const userReference = doc(db, "users", userID);
  await updateDoc(userReference, { language });
}

export async function getOwnedUserContent(userID: string) {
  const userReference = doc(db, "users", userID);
  // Get their content
  const contentQuery = query(
    collection(db, "content"),
    where("owner", "==", userReference),
    orderBy("name"),
  ).withConverter(contentConverter);
  const contentData = await getDocs(contentQuery);
  const contentFromDb = contentData.docs.map((doc) => doc.data());
  return contentFromDb;
}

/**
 * Create a new content item for a particular user
 * @param name The name of the new content to create
 * @param ownerID The ref for the owner of the content
 * @returns The FiresStore reference to the newly added content
 */
export async function createNewContent(name: string, ownerID: string) {
  assertRegisteredUser();
  const newContent = await addDoc(collection(db, "content"), {
    name: name,
    owner: doc(db, "users", ownerID),
  });
  return newContent;
}

/**
 * Add a template under the templates sub-collection of a particular content
 * @param template The entire template object to add to the content
 * @param contentID The content reference to use when adding the template
 * @returns The FireStore reference to the newly added template
 */
export async function addTemplateToContent(
  template: Template,
  contentID: string,
  contentName: string,
) {
  assertRegisteredUser();
  const newTemplate = await addDoc(
    collection(db, "content", contentID, "templates"),
    { ...template, contentID: contentID, contentName: contentName },
  );
  await updateTemplatesInfoForContent(contentID);
  return newTemplate;
}

export async function fetchContentTemplates(contentID: string) {
  const contentTemplates = await getDocs(
    collection(db, "content", contentID, "templates").withConverter(
      templateConverter,
    ),
  );
  return contentTemplates.docs.map((doc) => doc.data());
}

export async function updateTemplatesInfoForContent(contentID: string) {
  const contentTemplates = await getDocs(
    collection(db, "content", contentID, "templates").withConverter(
      templateConverter,
    ),
  );
  const templatesInfo = contentTemplates.docs.map((doc) => {
    const data = doc.data();
    return {
      id: doc.id,
      title: data.title,
      ...(data.description && {
        description: data.description,
      }),
      ...(data.copyOf && {
        copyOf: data.copyOf,
      }),
      ...(data.copyDate && {
        copyDate: data.copyDate,
      }),
    };
  });
  return await updateDoc(doc(db, "content", contentID), {
    templatesInfo: templatesInfo,
  });
}

/**
 *
 * @param template The template to save in Firestore
 * @returns The FireStore reference to the saved template
 */
export async function saveTemplate(template: Template) {
  assertRegisteredUser();
  if (!template.contentID || !template.id) {
    console.log("no content id or no template id found");
    throw Error("This template is not connected to content. Can not save it");
  } else {
    const savedTemplate = await updateDoc(
      doc(db, "content", template.contentID, "templates", template.id),
      template,
    );
    await updateTemplatesInfoForContent(template.contentID);
    return savedTemplate;
  }
}

export async function fetchTemplate(contentID: string, templateID: string) {
  const fetchedTemplate = await getDoc(
    doc(db, "content", contentID, "templates", templateID).withConverter(
      templateConverter,
    ),
  );
  return fetchedTemplate.data();
}

export async function fetchContent(contentID: string): Promise<Content | null> {
  const contentSnapshot = await getDoc(
    doc(db, "content", contentID).withConverter(contentConverter),
  );
  if (!contentSnapshot.exists()) {
    return null;
  }
  return contentSnapshot.data();
}

/**
 * Clones a marketplace content package and all its templates to a user's account.
 * Enforces provenance by referencing copyOf, setting copyDate, and marking public: false.
 */
export async function cloneContentToUserAccount(
  content: Content,
  templates: Template[],
  userID: string,
): Promise<{ newContentId: string; templateIdMap: Record<string, string> }> {
  const newContentRef = await addDoc(collection(db, "content"), {
    name: content.data.name,
    description: content.data.description || "",
    owner: doc(db, "users", userID),
    copyOf: doc(db, "content", content.id),
    copyDate: new Date(),
    public: false,
    price: 0,
    tags: content.data.tags || [],
    trusted: false,
  });

  const templateIdMap: Record<string, string> = {};

  for (const template of templates) {
    const templateCopy: Template = {
      ...template,
      contentID: newContentRef.id,
      contentName: content.data.name,
      ...(template.id && {
        copyOf: doc(db, "content", content.id, "templates", template.id),
        copyDate: new Date(),
      }),
    };
    delete templateCopy.id;

    const addedTemplateRef = await addDoc(
      collection(db, "content", newContentRef.id, "templates"),
      templateCopy,
    );

    if (template.id) {
      templateIdMap[template.id] = addedTemplateRef.id;
    }
  }

  await updateTemplatesInfoForContent(newContentRef.id);

  return {
    newContentId: newContentRef.id,
    templateIdMap,
  };
}
